import { RouteItem } from '@app/Routes';
import { FC, useEffect, useRef, useState } from 'react';
import ProjectPagesList from '@app/modules/projects/project-pages-list/ProjectPagesList';
import styles from './project-navigation-sidebar.module.scss';
import Sidebar, { SidebarModifier } from '@app/shared/components/sidebar/Sidebar';
import { useLocale } from '@app/core/hooks/useLocale';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { enumKeyByValue } from '@app/shared/helpers';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import { useLocation } from 'react-router-dom';

export const PARENT_CLASSNAME = 'project-navigation';

interface ProjectNavigationSidebarProps {
  collapsible?: boolean;
  collapsibleStorageKey?: string;
  containerClassName?: string;
  collapsedClassName?: string;
  containerTestId?: string;
}

const ProjectNavigationSidebar: FC<ProjectNavigationSidebarProps> = ({
  collapsible,
  collapsibleStorageKey,
  containerClassName,
  collapsedClassName,
  containerTestId,
}): JSX.Element => {
  const { l } = useLocale();
  const project = useAppSelector((state) => state.project.projectDraft);
  const { details: projectDetails } = project;
  const isOpm = projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.OPM);
  const findIndex = (route: RouteItem[], routeName: string) => {
    const index = route.findIndex((item) => item.route === routeName);
    return index;
  };

  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const getMainSection = (pathname: string): string | null => {
    const pathParts = pathname.split('/').filter(Boolean);
    const projectsIndex = pathParts.findIndex((part) => part === 'projects');

    if (projectsIndex !== -1 && pathParts.length > projectsIndex + 2) {
      return pathParts[projectsIndex + 2];
    }

    return null;
  };

  const [currentMainSection, setCurrentMainSection] = useState<string | null>(() =>
    getMainSection(location.pathname)
  );

  // Used to make sure that the selected sidebar menu is always visible when route changes
  useEffect(() => {
    const newMainSection = getMainSection(location.pathname);
    setCurrentMainSection(newMainSection);
  }, [location.pathname]);

  useEffect(() => {
    let rafId: number | null = null;
    const scroll = () => {
      if (!sidebarRef.current) {
        return;
      }

      const activeElement = sidebarRef.current.querySelector('a.active');

      if (activeElement) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const elementRect = activeElement.getBoundingClientRect();
        const isVisible =
          elementRect.top >= sidebarRect.top && elementRect.bottom <= sidebarRect.bottom;

        if (!isVisible) {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }
    };
    rafId = requestAnimationFrame(scroll);
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [currentMainSection]);

  const inputsRoutes: RouteItem[] = [
    {
      route: RouteConstants.ProjectDetails,
      title: l('_ProjectDetails'),
    },
    {
      route: RouteConstants.CapitalStructure,
      title: l('_CapitalStructure'),
    },
    {
      route: RouteConstants.DealThesis,
      title: l('_DealThesis'),
    },
    {
      route: RouteConstants.ValuationInputs,
      title: l('_ValuationInputs'),
    },
    {
      route: RouteConstants.OpmInputs,
      title: l('_OpmInputs'),
    },
    {
      route: RouteConstants.BenchmarkingInputs,
      title: l('_BenchmarkingInputs'),
    },
  ];

  let inputsRoutesToHide: string[] = [];
  if (projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM)) {
    inputsRoutesToHide = [...inputsRoutesToHide, RouteConstants.OpmInputs];
  }

  if (projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.OPM)) {
    inputsRoutesToHide = [
      ...inputsRoutesToHide,
      RouteConstants.DealThesis,
      RouteConstants.ValuationInputs,
    ];
  }

  inputsRoutesToHide.forEach((route) => {
    const index = findIndex(inputsRoutes, route);
    if (index !== -1) {
      inputsRoutes.splice(index, 1);
    }
  });

  const overallConclusionRoutes: RouteItem[] = [
    {
      route: RouteConstants.PwermOpmConclusions,
      title: l('_PwermOpm'),
    },
  ];

  const calculationOutputsRoutes: RouteItem[] = [
    {
      route: RouteConstants.DashboardPwerm,
      title: l('_PWERMDashboard'),
    },
    {
      route: RouteConstants.CostOfEquity,
      title: l('_CostOfEquityTitle'),
    },
    {
      route: RouteConstants.PwermWaterfall,
      title: l('_PWERMWaterfall'),
    },
  ];

  const calculationOutputsRoutesOPM: RouteItem[] = [
    {
      route: RouteConstants.DashboardOpm,
      title: l('_OPMDashboard'),
    },
    {
      route: RouteConstants.RealWorldDistributions,
      title: l('_RealWorldDistributions'),
    },
    {
      route: RouteConstants.OpmWaterfall,
      title: l('_OPMWaterfall'),
    },
  ];

  const otherCalculationsRoutes: RouteItem[] = [
    {
      route: RouteConstants.SponsorIRRAndHurdle,
      title: isOpm ? l('_IRRHurdle') : l('_SponsorIRRAndHurdle'),
    },
  ];

  return (
    <Sidebar
      ref={sidebarRef}
      data-testid="project-sidebar"
      sidebarModifier={SidebarModifier.Primary}
      collapsible={collapsible}
      collapsibleStorageKey={collapsibleStorageKey}
      containerClassName={containerClassName}
      collapsedClassName={collapsedClassName}
      containerTestId={containerTestId}>
      <div
        className={`${styles[`${PARENT_CLASSNAME}__heading`]} ${
          styles[`${PARENT_CLASSNAME}__heading--no-border-no-margin`]
        }`}>
        {l('_Inputs')}
      </div>
      <ProjectPagesList
        routes={inputsRoutes}
        className={styles[`${PARENT_CLASSNAME}__first-item`]}
      />
      <div className={styles[`${PARENT_CLASSNAME}__heading`]}>{l('_AnalysisAndConclusion')}</div>
      {projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) && (
        <ProjectPagesList
          subtitle={l('_OverallConclusion')}
          routes={overallConclusionRoutes}
          className={styles[`${PARENT_CLASSNAME}__second-item`]}
          indented
        />
      )}
      {projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM) ||
      projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) ? (
        <ProjectPagesList
          subtitle={l('_Pwerm')}
          routes={calculationOutputsRoutes}
          className={styles[`${PARENT_CLASSNAME}__second-item`]}
          indented
        />
      ) : null}
      {projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.OPM) ||
      projectDetails.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) ? (
        <ProjectPagesList
          subtitle={l('_Opm')}
          routes={calculationOutputsRoutesOPM}
          className={styles[`${PARENT_CLASSNAME}__first-item`]}
          indented
        />
      ) : null}
      <ProjectPagesList
        subtitle={l('_OtherCalculations')}
        routes={otherCalculationsRoutes}
        className={styles[`${PARENT_CLASSNAME}__second-item`]}
        indented
      />
    </Sidebar>
  );
};

export default ProjectNavigationSidebar;
